import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { 
  Package, IndianRupee, Calendar, ArrowLeft, 
  Loader2, ShoppingBag, BarChart3 
} from 'lucide-react';
import { Link } from 'react-router-dom';

const ProductWiseSales = () => {
  const [loading, setLoading] = useState(true);
  const [salesData, setSalesData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  const fetchProductSales = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.getProductWiseSales(selectedDate);
      setSalesData(response.data);
    } catch (error) {
      console.error('Error fetching product sales:', error);
      toast.error('Failed to load sales data');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchProductSales();
  }, [fetchProductSales]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading && !salesData) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto space-y-6 px-2 sm:px-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link 
            to="/" 
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} className="text-slate-600" />
          </Link>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-headings uppercase tracking-tight text-slate-900">
              Product-wise Sales
            </h1>
            <p className="text-sm sm:text-base text-slate-600 font-body mt-1">
              View sales breakdown by product
            </p>
          </div>
        </div>
        
        {/* Date Selector */}
        <div className="flex items-center gap-3">
          <Calendar size={20} className="text-slate-500" />
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            max={new Date().toISOString().split('T')[0]}
            className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
            className="px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          >
            Today
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 text-white">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white/20 rounded-lg">
              <IndianRupee size={24} />
            </div>
            <div>
              <p className="text-sm text-emerald-100">Total Revenue</p>
              <p className="text-3xl font-bold">{formatCurrency(salesData?.summary?.total_revenue)}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white/20 rounded-lg">
              <ShoppingBag size={24} />
            </div>
            <div>
              <p className="text-sm text-blue-100">Total Sales</p>
              <p className="text-3xl font-bold">{salesData?.summary?.total_sales || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 text-white">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white/20 rounded-lg">
              <Package size={24} />
            </div>
            <div>
              <p className="text-sm text-purple-100">Products Sold</p>
              <p className="text-3xl font-bold">{salesData?.summary?.products_sold || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Product-wise Breakdown */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2">
            <BarChart3 size={20} className="text-slate-600" />
            <h2 className="text-lg font-semibold text-slate-900">
              Sales by Product - {new Date(selectedDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            </h2>
          </div>
        </div>
        
        {salesData?.products?.length > 0 ? (
          <div className="divide-y divide-slate-100">
            {salesData.products.map((product, index) => (
              <div key={product.product_id || index} className="px-6 py-4 flex items-center justify-between hover:bg-slate-50">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                    index === 0 ? 'bg-amber-500' : 
                    index === 1 ? 'bg-slate-400' : 
                    index === 2 ? 'bg-amber-600' : 'bg-slate-300'
                  }`}>
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{product.product_name}</p>
                    <p className="text-sm text-slate-500">{product.category || 'Uncategorized'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-8">
                  <div className="text-right">
                    <p className="text-sm text-slate-500">Quantity</p>
                    <p className="text-lg font-semibold text-slate-900">{product.quantity}</p>
                  </div>
                  <div className="text-right min-w-[120px]">
                    <p className="text-sm text-slate-500">Revenue</p>
                    <p className="text-lg font-bold text-emerald-600">{formatCurrency(product.revenue)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-6 py-12 text-center text-slate-500">
            <Package className="mx-auto mb-3 text-slate-300" size={48} />
            <p className="text-lg font-medium">No sales on this date</p>
            <p className="text-sm mt-1">Select a different date to view sales data</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductWiseSales;
